import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ChevronDown, MapPin, Search, X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MESES_PT = [
  'JAN',
  'FEV',
  'MAR',
  'ABR',
  'MAI',
  'JUN',
  'JUL',
  'AGO',
  'SET',
  'OUT',
  'NOV',
  'DEZ'
];

const formatarMes = (mes = '') => {
  const [ano, mesNumero] = String(mes).split('-');
  const idx = Number(mesNumero) - 1;
  if (!ano || Number.isNaN(idx) || idx < 0 || idx > 11) return mes;
  return `${MESES_PT[idx]}/${String(ano).slice(-2)}`;
};

const formatarMoeda = (valor = 0) =>
  Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const lerQtdOS = (item = {}) => {
  const qtd =
    item.qtd_os ??
    item.qtdOS ??
    item['qtd_o.s.'] ??
    item['qtd_o.s'] ??
    item['qtd_o_s'] ??
    item['qtd o.s.'] ??
    item['qtd o.s'];

  return Number(qtd || 0);
};

const TooltipCustom = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0]?.payload;
  if (!item) return null;

  return (
    <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg shadow-sm p-3 text-sm">
      <p className="font-semibold text-[color:var(--text-primary)] mb-1">{label}</p>
      <p className="text-[color:var(--text-muted)]">Custo mensal: {formatarMoeda(item.custoMensal)}</p>
      <p className="text-[color:var(--text-muted)]">Custo médio por O.S.: {formatarMoeda(item.custoMedio)}</p>
      <p className="text-[color:var(--text-muted)]">O.S. no mês: {Number(item.totalOS || 0).toLocaleString('pt-BR')}</p>
    </div>
  );
};

const EvolucaoCustoMensalChart = ({ data = [], loading = false }) => {
  const [cidadeSelecionada, setCidadeSelecionada] = useState('todas');
  const [dropdownCidadeAberto, setDropdownCidadeAberto] = useState(false);
  const [buscaCidade, setBuscaCidade] = useState('');
  const dropdownCidadeRef = useRef(null);

  const cidades = useMemo(() => {
    const unicas = [...new Set((data || []).map((item) => item.cidade).filter(Boolean))];
    return unicas.sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [data]);

  useEffect(() => {
    if (cidadeSelecionada !== 'todas' && !cidades.includes(cidadeSelecionada)) {
      setCidadeSelecionada('todas');
    }
  }, [cidades, cidadeSelecionada]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownCidadeRef.current && !dropdownCidadeRef.current.contains(event.target)) {
        setDropdownCidadeAberto(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cidadesFiltradas = useMemo(() => {
    if (!buscaCidade.trim()) return cidades;
    const termo = buscaCidade.toLowerCase().trim();
    return cidades.filter((cidade) => cidade.toLowerCase().includes(termo));
  }, [cidades, buscaCidade]);

  const dadosGrafico = useMemo(() => {
    const base = cidadeSelecionada === 'todas'
      ? data
      : (data || []).filter((item) => item.cidade === cidadeSelecionada);

    const agregados = new Map();

    base.forEach((item) => {
      const mes = item.mes;
      const custo = Number(item.custo || 0);
      const qtdOS = lerQtdOS(item);

      if (!mes) return;

      const atual = agregados.get(mes) || { custoMensal: 0, totalOS: 0 };
      atual.custoMensal += custo;
      atual.totalOS += qtdOS;
      agregados.set(mes, atual);
    });

    return Array.from(agregados.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mes, valores]) => ({
        mes,
        mesLabel: formatarMes(mes),
        custoMensal: valores.custoMensal,
        custoMedio: valores.totalOS > 0 ? valores.custoMensal / valores.totalOS : 0,
        totalOS: valores.totalOS
      }));
  }, [data, cidadeSelecionada]);

  return (
    <div className="bg-[color:var(--surface-muted)] rounded-lg border border-[color:var(--border)] p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <div>
          <h4 className="text-sm font-semibold text-[color:var(--text-primary)]">Evolução do Custo Mensal e Custo Médio (R$)</h4>
          <p className="text-xs text-[color:var(--text-muted)]">Últimos meses dentro do período selecionado</p>
        </div>

        <div ref={dropdownCidadeRef} className="relative w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setDropdownCidadeAberto((prev) => !prev)}
            className={`w-full sm:w-auto inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] ${
              cidadeSelecionada !== 'todas'
                ? 'border-[color:var(--accent)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
                : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
            }`}
          >
            <MapPin className="h-4 w-4" />
            <span className="truncate">
              {cidadeSelecionada === 'todas' ? 'Filtrar por Cidade' : '1 cidade selecionada'}
            </span>
            {cidadeSelecionada !== 'todas' && (
              <span className="inline-flex items-center rounded-full border border-[color:var(--accent)] bg-[color:var(--surface-muted)] px-2 py-0.5 text-xs font-medium text-[color:var(--accent)]">
                1
              </span>
            )}
            <ChevronDown className={`h-4 w-4 text-[color:var(--text-muted)] transition-transform ${dropdownCidadeAberto ? 'rotate-180' : ''}`} />
          </button>

          {dropdownCidadeAberto && (
            <div className="absolute z-50 mt-2 w-64 rounded-md bg-[color:var(--surface)] shadow-sm border border-[color:var(--border)] focus:outline-none right-0">
              <div className="p-2 border-b border-[color:var(--border)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[color:var(--text-primary)]">Filtrar por Cidade</span>
                  {cidadeSelecionada !== 'todas' && (
                    <button
                      onClick={() => {
                        setCidadeSelecionada('todas');
                        setBuscaCidade('');
                      }}
                      className="text-xs text-[color:var(--accent)] hover:opacity-80"
                    >
                      Limpar
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                  <input
                    type="text"
                    value={buscaCidade}
                    onChange={(e) => setBuscaCidade(e.target.value)}
                    placeholder="Buscar cidade..."
                    className="w-full pl-8 pr-8 py-1.5 text-sm border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                  />
                  {buscaCidade && (
                    <button
                      onClick={() => setBuscaCidade('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)]"
                      type="button"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>

              <div className="max-h-64 overflow-auto p-1">
                <button
                  type="button"
                  onClick={() => {
                    setCidadeSelecionada('todas');
                    setDropdownCidadeAberto(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-[color:var(--surface-muted)] cursor-pointer ${
                    cidadeSelecionada === 'todas' ? 'bg-[color:var(--surface-muted)] font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'
                  }`}
                >
                  Todas as cidades
                </button>

                {cidadesFiltradas.length > 0 ? (
                  cidadesFiltradas.map((cidade) => {
                    const isSelected = cidadeSelecionada === cidade;
                    return (
                      <button
                        key={cidade}
                        type="button"
                        onClick={() => {
                          setCidadeSelecionada(cidade);
                          setDropdownCidadeAberto(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-[color:var(--surface-muted)] cursor-pointer ${
                          isSelected ? 'bg-[color:var(--surface-muted)] font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'
                        }`}
                      >
                        {cidade}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">Nenhuma cidade encontrada</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="h-72 flex items-center justify-center text-[color:var(--text-muted)] text-sm">
          Carregando evolução de custos...
        </div>
      ) : dadosGrafico.length === 0 ? (
        <div className="h-72 flex items-center justify-center text-[color:var(--text-muted)] text-sm">
          Sem dados de evolução para o período/filtros selecionados.
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dadosGrafico} margin={{ top: 10, right: 26, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="mesLabel"
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={{ stroke: 'var(--border)' }}
              />
              <YAxis
                yAxisId="left"
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={{ stroke: 'var(--border)' }}
                tickFormatter={(v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={{ stroke: 'var(--border)' }}
                tickFormatter={(v) => Number(v || 0).toLocaleString('pt-BR')}
              />
              <Tooltip content={<TooltipCustom />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="custoMensal"
                name="Custo mensal (R$)"
                stroke="var(--accent)"
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="custoMedio"
                name="Custo médio por O.S. (R$)"
                stroke="#334155"
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default EvolucaoCustoMensalChart;
